// src/services/azureResearchFilesService.js
// Liest Dateien aus dem Blob-Container "research-files" und liefert sie in
// derselben Form wie die Tabellen-Quellen, damit sie im Research-Tool gesucht
// und Fragen zugeordnet werden können.
//
// Mehr-Projekt-Fähigkeit: Jedes Research-Projekt hat ein Ordner-Präfix im
// Container (project.filesPrefix, z.B. "rp_xyz/"). Das Alt-Projekt (Sakralbauten)
// nutzt die Container-Wurzel (Präfix "") — dort werden die Ordner der neueren
// Projekte über excludePrefixes ausgeblendet, damit sich Projekte nicht vermischen.
//
// Der Container ist privat (publicAccess: None) -> Blobs benötigen den Blob-SAS
// sowohl zum Auflisten als auch zur Anzeige. Es wird derselbe (bereits im Bundle
// vorhandene) Blob-SAS wie in azureBlobService.js genutzt.

const AZURE_BLOB_CONFIG = {
  storageAccount: "mfprojects",
  containerName: "research-files",
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

const baseUrl = (blobName) =>
  `https://${AZURE_BLOB_CONFIG.storageAccount}.blob.core.windows.net/${AZURE_BLOB_CONFIG.containerName}/${blobName}`;

// URL inkl. SAS (für <img src> und zum Öffnen).
const withSas = (blobName) => `${baseUrl(blobName)}?${AZURE_BLOB_CONFIG.sasToken}`;

const tag = (el, name) => el.getElementsByTagName(name)[0]?.textContent || "";

// Listet alle Blobs des Containers (mit Marker-Pagination), optional auf ein
// Ordner-Präfix eingeschränkt (serverseitig via prefix-Parameter).
const listBlobs = async (prefix = "") => {
  let blobs = [];
  let marker = "";
  do {
    let url = `${baseUrl("").replace(/\/$/, "")}?restype=container&comp=list&${AZURE_BLOB_CONFIG.sasToken}`;
    if (prefix) url += `&prefix=${encodeURIComponent(prefix)}`;
    if (marker) url += `&marker=${encodeURIComponent(marker)}`;
    const res = await fetch(url);
    if (!res.ok) {
      const txt = await res.text();
      console.error("List research-files Error:", res.status, txt);
      throw new Error(`Blob List Error: ${res.status}`);
    }
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, "application/xml");
    const blobEls = Array.from(doc.getElementsByTagName("Blob"));
    blobs = blobs.concat(
      blobEls.map((el) => ({
        name: tag(el, "Name"),
        lastModified: tag(el, "Last-Modified"),
        contentType: tag(el, "Content-Type"),
      }))
    );
    marker = doc.getElementsByTagName("NextMarker")[0]?.textContent || "";
  } while (marker);
  return blobs.filter((b) => b.name);
};

const SUFFIX_RE = /_(original|thumb|xl|large|medium|thumbnail)\.[^./]+$/i;
const isThumb = (name) => /_(thumb|thumbnail|medium)\.[^./]+$/i.test(name);
const isOriginal = (name) => /_(original|xl|large)\.[^./]+$/i.test(name);

// Gruppiert original/thumb-Paare zu je einer logischen Datei und liefert
// Quellen-Objekte (gleiche Form wie mapSource in azureResearchService).
//  - prefix:          nur Blobs dieses Ordners listen ("" = ganzer Container)
//  - excludePrefixes: Blobs dieser Ordner ausblenden (Ordner ANDERER Projekte,
//                     nur relevant für das Wurzel-Projekt mit prefix "")
export const loadResearchFiles = async (prefix = "", excludePrefixes = []) => {
  try {
    const blobs = (await listBlobs(prefix)).filter(
      (b) => !excludePrefixes.some((p) => p && b.name.startsWith(p))
    );
    const groups = new Map();

    for (const b of blobs) {
      const base = b.name.replace(SUFFIX_RE, "");
      if (!groups.has(base)) groups.set(base, { thumb: null, original: null, any: null });
      const g = groups.get(base);
      g.any = g.any || b;
      if (isThumb(b.name)) g.thumb = b;
      else if (isOriginal(b.name)) g.original = b;
      else g.original = g.original || b;
    }

    const sources = [];
    for (const [base, g] of groups.entries()) {
      const full = g.original || g.any || g.thumb;
      const thumb = g.thumb || full;
      if (!full) continue;
      const segments = base.split("/");
      const fileName = segments[segments.length - 1] || base;
      const folder = segments.length > 1 ? segments[0] : "";
      sources.push({
        kind: "file",
        // rk dient als global eindeutiger Schlüssel (analog Tabellen-RowKey) für
        // Suche, Anzeige und Verknüpfung. Der volle Blob-Name ist eindeutig.
        rk: full.name,
        pk: "",
        title: fileName,
        content: "",
        url: withSas(full.name),
        favicon: "",
        thumbnailData: withSas(thumb.name),
        blobUrl: withSas(full.name),
        tags: folder,
        thema: "Research-Datei",
        type: "file",
        lastModified: full.lastModified || "",
      });
    }

    sources.sort((a, b) => (b.lastModified || "").localeCompare(a.lastModified || ""));
    return sources;
  } catch (error) {
    console.error("Load Research Files Error:", error);
    return [];
  }
};
